import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Divider,
    Typography,
    CircularProgress,
    Box
} from '@mui/material';
import { execScript } from '../../http/complexesApi.js';
import { addNotification } from '../../store/notificationSlice';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StopIcon from '@mui/icons-material/Stop';
import SettingsIcon from '@mui/icons-material/Settings';
import TerminalIcon from '@mui/icons-material/Terminal';

const PopupScriptsDropdown = ({ host, hostType = 'default' }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [loadingScript, setLoadingScript] = useState(null);
    const open = Boolean(anchorEl);
    const dispatch = useDispatch();

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const getScripts = () => {
        switch (hostType) {
            case 'crossroad':
                const scripts = [];
                if (host.child) {
                    host.child.forEach(child => {
                        if (child.scripts) {
                            child.scripts.forEach(script => {
                                if (script.type === '0' && script.description === 'map') {
                                    scripts.push({
                                        id: script.id,
                                        host: child,
                                        name: script.name
                                    });
                                }
                            });
                        }
                    });
                }
                return scripts;

            case 'speed':
            case 'vgk':
                const speedScripts = [];
                if (host.scripts) {
                    host.scripts.forEach(script => {
                        if (script.type === '0' && script.description === 'map') {
                            speedScripts.push({
                                id: script.id,
                                name: script.name
                            });
                        }
                    });
                }
                return speedScripts;

            default:
                const defaultScripts = [];
                if (host.scripts) {
                    host.scripts.forEach(script => {
                        if (script.type === '0' && script.description === 'map') {
                            defaultScripts.push({
                                id: script.id,
                                name: script.name
                            });
                        }
                    });
                }
                return defaultScripts;
        }
    };

    const scripts = getScripts();

    if (!scripts || scripts.length === 0) {
        return null;
    }

    const handleScriptClick = (script) => {
        setLoadingScript(script.id);

        dispatch(addNotification({
            message: 'Выполнение команды, дождитесь завершения',
            options: { 
                variant: 'info',
                title: script.host?.name || host.name
            }
        }));

        execScript(script.host || host, script, (type, message) => {
            setLoadingScript(null);
            
            let variant = 'info';
            if (type === 'success') variant = 'success';
            else if (type === 'error') variant = 'error';
            else if (type === 'warning') variant = 'warning';
            
            dispatch(addNotification({
                message: message?.body || message || 'Команда выполнена',
                options: { 
                    variant: variant,
                    title: message?.head || script.host?.name || host.name
                }
            }));
        });

        handleClose();
    };

    const getScriptIcon = (script) => {
        const name = script.name.toLowerCase();

        if (name.includes('restart') || name.includes('перезапуск') || name.includes('reboot')) {
            return <RestartAltIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--warning-color)' }} />;
        }

        if (name.includes('stop') || name.includes('стоп') || name.includes('остановка')) {
            return <StopIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--error-color)' }} />;
        }

        if (name.includes('start') || name.includes('запуск') || name.includes('старт')) {
            return <PlayArrowIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--success-color)' }} />;
        }

        if (name.includes('config') || name.includes('конфиг') || name.includes('настройка')) {
            return <SettingsIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--info-color)' }} />;
        }

        return <TerminalIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--primary-color)' }} />;
    };

    return (
        <>
            <Tooltip title="Выполнить команду" arrow placement="top">
                <Box
                    onClick={handleClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: '3px', md: '4px' },
                        padding: { xs: '3px 6px', md: '4px 8px' },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: 'var(--background-paper)',
                        border: '1px solid var(--divider-color)',
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        '&:hover': {
                            backgroundColor: 'var(--warning-color)',
                            borderColor: 'var(--warning-color)',
                            '& .btn-icon, & .btn-label': {
                                color: 'white',
                            },
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        }
                    }}
                >
                    <TerminalIcon 
                        className="btn-icon" 
                        sx={{ 
                            fontSize: { xs: '12px', md: '14px' }, 
                            color: 'var(--warning-color)',
                            transition: 'color 150ms'
                        }} 
                    />
                    <Typography 
                        className="btn-label"
                        sx={{ 
                            fontSize: { xs: '9px', md: '10px' }, 
                            fontWeight: 600, 
                            color: 'var(--warning-color)',
                            whiteSpace: 'nowrap',
                            transition: 'color 150ms'
                        }}
                    >
                        Команды
                    </Typography>
                </Box>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: { xs: '160px', md: '180px' },
                            maxWidth: { xs: '320px', md: '360px' },
                            borderRadius: { xs: '6px', md: '8px' },
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            border: '1px solid var(--divider-color)',
                            backgroundColor: 'var(--background-paper)',
                            '& .MuiList-root': {
                                padding: { xs: '3px', md: '4px' },
                            }
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                <Typography
                    sx={{
                        px: { xs: 1, md: 1.5 },
                        py: { xs: 0.4, md: 0.5 },
                        fontSize: { xs: '9px', md: '10px' },
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}
                >
                    Команды управления
                </Typography>
                <Divider sx={{ my: { xs: 0.4, md: 0.5 } }} />
                {scripts.map((script, index) => (
                    <MenuItem
                        key={script.id || index}
                        onClick={() => handleScriptClick(script)}
                        disabled={loadingScript === script.id}
                        sx={{
                            borderRadius: { xs: '3px', md: '4px' },
                            py: { xs: 0.5, md: 0.75 },
                            px: { xs: 0.75, md: 1 },
                            minHeight: 'auto',
                            '&:hover': {
                                backgroundColor: 'var(--background-default)',
                            },
                            '&.Mui-disabled': {
                                opacity: 0.7,
                            }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: { xs: '24px', md: '28px' } }}>
                            {loadingScript === script.id ? (
                                <CircularProgress 
                                    size={{ xs: 12, md: 14 }} 
                                    thickness={4} 
                                    sx={{ color: 'var(--primary-color)' }} 
                                />
                            ) : (
                                getScriptIcon(script)
                            )}
                        </ListItemIcon>
                        <ListItemText
                            primary={`${script.name}${script.host ? ` — ${script.host.name.split(' / ')[1]}` : ''}`}
                            slotProps={{
                                primary: {
                                    sx: {
                                        fontSize: { xs: '11px', md: '12px' },
                                        fontWeight: 500,
                                        color: 'var(--text-primary)'
                                    }
                                }
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default PopupScriptsDropdown;
